// components/Admin/NewInvoice.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PDFDownloadLink, BlobProvider } from '@react-pdf/renderer';
import axios from 'axios';
import InvoicePDFDocument from './InvoicePDF';

const API_URL = 'http://localhost:5000/api';

const NewInvoice = () => {
  const navigate = useNavigate();
  const [itemCount, setItemCount] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  
  // Company details state
  const [companyDetails, setCompanyDetails] = useState({
    companyName: 'Karmic Nexus IT Solutions',
    logo: '/karmic-logo.png',
    logoData: null,
    addressLine1: 'Plot no. 98, Omkar nagar, Manewada road,',
    addressLine2: 'Nagpur - 440027',
    gstin: '27ERZPA9595L1ZI',
    msme: 'UDYAM-MH-20-0184835',
    bankName: 'Kotak Mahindra Bank',
    accountNo: '7947486959',
    branchIfsc: 'Medical Square, IFSC- KKBK0001839',
    accountHolder: 'Ms. Manjiri Dinesh Alone',
    contact: 'your-contact-here',
    email: 'your-email@karmic.in',
    workingHours: 'Mon-Sat: 10:00 AM - 6:00 PM',
    qrCodeData: null
  });

  const [formData, setFormData] = useState({
    invoiceNumber: `INV-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    clientName: '',
    clientAddress: '',
    clientGST: '',
    placeOfSupply: '',
    amountInWords: '',
    subTotal: '0',
    sgstAmount: '0',
    cgstAmount: '0',
    cessAmount: '0',
    totalAmount: '0',
    note: 'Kindly note that this invoice for Services Provided remains unpaid. We request at least 50% payment to avoid service interruptions.',
    items: [
      {
        description: '',
        hsn: '',
        qty: '1',
        rate: '0',
        sgst: '9',
        cgst: '9',
        cess: '0',
        amount: '0'
      }
    ],
    status: 'unpaid'
  });

  const previewRef = useRef(null);
  const fileInputRef = useRef(null);
  const logoFileInputRef = useRef(null);
  const [pdfError, setPdfError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [backendStatus, setBackendStatus] = useState('checking');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showCompanySettings, setShowCompanySettings] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showDownloadLink, setShowDownloadLink] = useState(false);

  // Blue Sky Energy theme colors
  const theme = {
    primary: '#447793',
    secondary: '#2D5A74',
    dark: '#123249',
    light: '#DDE8ED',
    white: '#ffffff',
    text: '#123249'
  };

  // Styles (copy all your existing styles from old Invoice.js)
  const styles = {
    container: {
      fontFamily: "'Cambria', 'Segoe UI', serif",
      backgroundColor: '#f5f7fa',
      minHeight: '100vh',
      color: theme.text,
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      boxSizing: 'border-box'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '30px',
      paddingBottom: '15px',
      borderBottom: `2px solid ${theme.primary}`,
      flexWrap: 'wrap',
      gap: '15px'
    },
    headerTitle: {
      fontSize: 'clamp(1.2rem, 4vw, 2rem)',
      color: theme.dark,
      fontWeight: 600,
      margin: 0
    },
    headerActions: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap'
    },
    btn: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
      transition: 'all 0.3s ease',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    btnOutlinePrimary: {
      background: 'transparent',
      border: `1px solid ${theme.primary}`,
      color: theme.primary
    },
    btnPrimary: {
      background: theme.primary,
      color: 'white'
    },
    btnSuccess: {
      background: theme.secondary,
      color: 'white'
    },
    btnDanger: {
      background: '#e74c3c',
      color: 'white'
    },
    btnWarning: {
      background: '#f39c12',
      color: 'white'
    },
    btnInfo: {
      background: '#3498db',
      color: 'white'
    },
    btnSm: {
      padding: '6px 12px',
      fontSize: '12px'
    },
    invoiceContainer: {
      background: 'white',
      borderRadius: '10px',
      padding: '30px',
      boxShadow: '0 4px 12px rgba(6,18,33,0.1)',
      marginBottom: '30px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 500,
      color: theme.dark
    },
    input: {
      width: '100%',
      padding: '12px 15px',
      border: `1px solid ${theme.light}`,
      borderRadius: '5px',
      fontSize: '14px',
      transition: 'border-color 0.3s ease',
      outline: 'none',
      boxSizing: 'border-box'
    },
    inputError: {
      borderColor: '#e74c3c'
    },
    textarea: {
      width: '100%',
      padding: '12px 15px',
      border: `1px solid ${theme.light}`,
      borderRadius: '5px',
      fontSize: '14px',
      transition: 'border-color 0.3s ease',
      outline: 'none',
      resize: 'vertical',
      minHeight: '80px',
      boxSizing: 'border-box'
    },
    grid2: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      marginBottom: '20px'
    },
    grid3: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '15px',
      marginBottom: '20px'
    },
    grid21: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px'
    },
    tableResponsive: {
      overflowX: 'auto',
      marginBottom: '20px',
      WebkitOverflowScrolling: 'touch'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      border: `1px solid ${theme.light}`,
      minWidth: '800px'
    },
    th: {
      padding: '12px 15px',
      textAlign: 'left',
      borderBottom: `1px solid ${theme.light}`,
      fontWeight: 600,
      color: theme.dark,
      fontSize: '14px',
      backgroundColor: theme.light,
      whiteSpace: 'nowrap'
    },
    td: {
      padding: '12px 15px',
      borderBottom: `1px solid ${theme.light}`
    },
    inputSm: {
      width: '100%',
      padding: '8px 10px',
      border: `1px solid ${theme.light}`,
      borderRadius: '4px',
      fontSize: '13px',
      outline: 'none',
      boxSizing: 'border-box'
    },
    textCenter: {
      textAlign: 'center'
    },
    mb3: {
      marginBottom: '15px'
    },
    mb4: {
      marginBottom: '20px'
    },
    mt4: {
      marginTop: '20px'
    },
    hidden: {
      display: 'none'
    },
    invoicePreview: {
      border: `1px solid ${theme.light}`,
      padding: '20px',
      backgroundColor: 'white',
      marginTop: '20px',
      borderRadius: '10px',
      overflowX: 'auto'
    },
    historySection: {
      background: 'white',
      borderRadius: '10px',
      padding: '20px',
      marginTop: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    historyItem: {
      border: `1px solid ${theme.light}`,
      borderRadius: '5px',
      padding: '15px',
      marginBottom: '10px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative'
    },
    statusBadge: {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 500,
      display: 'inline-block'
    },
    statusPaid: {
      background: '#d4edda',
      color: '#155724'
    },
    statusUnpaid: {
      background: '#fff3cd',
      color: '#856404'
    },
    statusOverdue: {
      background: '#f8d7da',
      color: '#721c24'
    },
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999
    },
    loadingContent: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '10px',
      textAlign: 'center'
    },
    errorMessage: {
      backgroundColor: '#fee',
      color: '#c00',
      padding: '10px',
      borderRadius: '5px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    successMessage: {
      backgroundColor: '#d4edda',
      color: '#155724',
      padding: '10px',
      borderRadius: '5px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    validationErrors: {
      backgroundColor: '#fff3cd',
      color: '#856404',
      padding: '10px',
      borderRadius: '5px',
      marginBottom: '20px'
    },
    mobileNote: {
      display: 'none',
      textAlign: 'center',
      color: theme.secondary,
      fontSize: '12px',
      marginBottom: '10px'
    },
    actionButtons: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap',
      justifyContent: 'center'
    },
    tabContainer: {
      display: 'flex',
      gap: '10px',
      marginBottom: '20px',
      borderBottom: `2px solid ${theme.light}`,
      paddingBottom: '10px'
    },
    tab: {
      padding: '10px 20px',
      cursor: 'pointer',
      borderRadius: '5px 5px 0 0',
      transition: 'all 0.3s ease'
    },
    tabActive: {
      background: theme.primary,
      color: 'white'
    },
    tabInactive: {
      background: 'transparent',
      color: theme.text
    },
    loadingSpinner: {
      display: 'inline-block',
      width: '20px',
      height: '20px',
      border: '2px solid #f3f3f3',
      borderTopColor: theme.primary,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    companySettings: {
      background: theme.light,
      borderRadius: '10px',
      padding: '20px',
      marginBottom: '20px'
    },
    companySettingsHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '15px',
      cursor: 'pointer'
    },
    deleteConfirmOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000
    },
    deleteConfirmDialog: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '10px',
      maxWidth: '400px',
      textAlign: 'center'
    },
    qrCodePreview: {
      width: '150px',
      height: '150px',
      border: `2px solid ${theme.primary}`,
      borderRadius: '10px',
      objectFit: 'contain',
      backgroundColor: 'white',
      padding: '5px'
    },
    qrCodeUploadArea: {
      border: `2px dashed ${theme.primary}`,
      borderRadius: '10px',
      padding: '20px',
      textAlign: 'center',
      cursor: 'pointer',
      backgroundColor: 'white',
      transition: 'all 0.3s ease'
    },
    fileInput: {
      display: 'none'
    },
    logoPreview: {
      width: '80px',
      height: '80px',
      border: `2px solid ${theme.primary}`,
      borderRadius: '10px',
      objectFit: 'contain',
      backgroundColor: 'white',
      padding: '5px',
      marginRight: '15px'
    },
    logoUploadArea: {
      border: `2px dashed ${theme.primary}`,
      borderRadius: '10px',
      padding: '15px',
      textAlign: 'center',
      cursor: 'pointer',
      backgroundColor: 'white',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '15px',
      minHeight: '100px'
    },
    logoSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      flexWrap: 'wrap'
    },
    logoInput: {
      flex: 1,
      minWidth: '250px'
    },
    downloadLinkContainer: {
      marginTop: '10px',
      textAlign: 'center'
    }
  };

  // Copy ALL your existing functions from Invoice.js:
  // loadCompanyDetails, saveCompanyDetails, handleCompanyDetailChange,
  // handleLogoUpload, handleLogoPathChange, removeLogo,
  // handleQRCodeUpload, removeQRCode, checkBackendConnection,
  // loadInvoiceHistory, loadLocalInvoiceHistory, deleteInvoice,
  // confirmDelete, cancelDelete, validateForm, handleInputChange,
  // handleItemChange, calculateTotals, addItem, removeItem,
  // numberToWords, formatDate, formatDateTime, previewInvoice,
  // editInvoice, viewInvoiceFromHistory, getStatusBadgeStyle, resetForm

  // I'll include the most important ones here, but you should copy all of them from your old file

  const loadCompanyDetails = () => {
    try {
      const savedDetails = localStorage.getItem('companyDetails');
      if (savedDetails) {
        setCompanyDetails(JSON.parse(savedDetails));
      }
    } catch (error) {
      console.error('Error loading company details:', error);
    }
  };

  const saveCompanyDetails = () => {
    try {
      localStorage.setItem('companyDetails', JSON.stringify(companyDetails));
      alert('Company details saved successfully!');
    } catch (error) {
      console.error('Error saving company details:', error);
    }
  };

  const handleCompanyDetailChange = (field, value) => {
    setCompanyDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        alert('File size should be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setCompanyDetails(prev => ({
          ...prev,
          logoData: event.target.result,
          logo: ''
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoPathChange = (e) => {
    setCompanyDetails(prev => ({
      ...prev,
      logo: e.target.value,
      logoData: null
    }));
  };

  const removeLogo = () => {
    setCompanyDetails(prev => ({
      ...prev,
      logo: '/karmic-logo.png',
      logoData: null
    }));
    if (logoFileInputRef.current) {
      logoFileInputRef.current.value = '';
    }
  };

  const handleQRCodeUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        alert('File size should be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setCompanyDetails(prev => ({
          ...prev,
          qrCodeData: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeQRCode = () => {
    setCompanyDetails(prev => ({
      ...prev,
      qrCodeData: null
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const checkBackendConnection = async () => {
    try {
      await axios.get(`${API_URL}/health`);
      setBackendStatus('connected');
      loadInvoiceHistory();
    } catch (error) {
      console.log('Backend not connected - running in offline mode');
      setBackendStatus('offline');
      loadLocalInvoiceHistory();
    }
  };

  const loadInvoiceHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API_URL}/invoices`);
      let invoices = [];
      if (Array.isArray(response.data)) {
        invoices = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        invoices = response.data.data;
      }
      setInvoiceHistory(invoices);
      localStorage.setItem('invoiceHistory', JSON.stringify(invoices));
    } catch (error) {
      console.error('Error loading invoice history:', error);
      loadLocalInvoiceHistory();
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadLocalInvoiceHistory = () => {
    try {
      const localInvoices = JSON.parse(localStorage.getItem('invoiceHistory') || '[]');
      setInvoiceHistory(Array.isArray(localInvoices) ? localInvoices : []);
    } catch (error) {
      console.error('Error loading local invoice history:', error);
      setInvoiceHistory([]);
    }
  };

  const deleteInvoice = async (invoiceId, e) => {
    e.stopPropagation();
    setDeleteConfirm(invoiceId);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setIsGenerating(true);
    try {
      if (backendStatus === 'connected') {
        await axios.delete(`${API_URL}/invoices/${deleteConfirm}`);
      }
      const updatedHistory = invoiceHistory.filter(inv => (inv._id || inv.id) !== deleteConfirm);
      setInvoiceHistory(updatedHistory);
      localStorage.setItem('invoiceHistory', JSON.stringify(updatedHistory));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Error deleting invoice');
    } finally {
      setDeleteConfirm(null);
      setIsGenerating(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const validateForm = () => {
    const errors = [];
    if (!formData.clientName.trim()) {
      errors.push('Client name is required');
    }
    if (!formData.clientAddress.trim()) {
      errors.push('Client address is required');
    }
    if (!formData.placeOfSupply.trim()) {
      errors.push('Place of supply is required');
    }
    formData.items.forEach((item, index) => {
      if (!item.description.trim()) {
        errors.push(`Item ${index + 1}: Description is required`);
      }
      if (parseFloat(item.qty) <= 0) {
        errors.push(`Item ${index + 1}: Quantity must be greater than 0`);
      }
      if (parseFloat(item.rate) < 0) {
        errors.push(`Item ${index + 1}: Rate cannot be negative`);
      }
    });
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    setValidationErrors([]);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;
    
    const qty = parseFloat(updatedItems[index].qty) || 0;
    const rate = parseFloat(updatedItems[index].rate) || 0;
    const sgst = parseFloat(updatedItems[index].sgst) || 0;
    const cgst = parseFloat(updatedItems[index].cgst) || 0;
    const cess = parseFloat(updatedItems[index].cess) || 0;
    
    const baseAmount = qty * rate;
    const sgstAmount = baseAmount * (sgst / 100);
    const cgstAmount = baseAmount * (cgst / 100);
    const cessAmount = baseAmount * (cess / 100);
    const totalAmount = baseAmount + sgstAmount + cgstAmount + cessAmount;
    
    updatedItems[index].amount = totalAmount.toFixed(2);
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    calculateTotals(updatedItems);
    setValidationErrors([]);
  };

  const calculateTotals = (items) => {
    let subTotal = 0;
    let sgstTotal = 0;
    let cgstTotal = 0;
    let cessTotal = 0;
    
    items.forEach(item => {
      const qty = parseFloat(item.qty) || 0;
      const rate = parseFloat(item.rate) || 0;
      const sgst = parseFloat(item.sgst) || 0;
      const cgst = parseFloat(item.cgst) || 0;
      const cess = parseFloat(item.cess) || 0;
      
      const baseAmount = qty * rate;
      subTotal += baseAmount;
      sgstTotal += baseAmount * (sgst / 100);
      cgstTotal += baseAmount * (cgst / 100);
      cessTotal += baseAmount * (cess / 100);
    });
    
    const totalAmount = subTotal + sgstTotal + cgstTotal + cessTotal;
    
    setFormData(prev => ({
      ...prev,
      subTotal: subTotal.toFixed(2),
      sgstAmount: sgstTotal.toFixed(2),
      cgstAmount: cgstTotal.toFixed(2),
      cessAmount: cessTotal.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      amountInWords: numberToWords(totalAmount)
    }));
  };

  const addItem = () => {
    const newItem = {
      description: '',
      hsn: '',
      qty: '1',
      rate: '0',
      sgst: '9',
      cgst: '9',
      cess: '0',
      amount: '0'
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setItemCount(prev => prev + 1);
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const updatedItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
      setItemCount(prev => prev - 1);
      calculateTotals(updatedItems);
    } else {
      alert("You need at least one item in the invoice.");
    }
  };

  const numberToWords = (num) => {
    if (num === 0) return 'Zero Rupees Only';
    
    const number = Math.floor(num);
    const decimal = Math.round((num - number) * 100);

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const numToWords = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + ' ' + ones[n % 10];
      return ones[Math.floor(n / 100)] + ' Hundred ' + numToWords(n % 100);
    };

    let words = '';
    let remaining = number;
    
    if (remaining >= 10000000) {
      words += numToWords(Math.floor(remaining / 10000000)) + ' Crore ';
      remaining %= 10000000;
    }
    if (remaining >= 100000) {
      words += numToWords(Math.floor(remaining / 100000)) + ' Lakh ';
      remaining %= 100000;
    }
    if (remaining >= 1000) {
      words += numToWords(Math.floor(remaining / 1000)) + ' Thousand ';
      remaining %= 1000;
    }
    if (remaining >= 100) {
      words += numToWords(Math.floor(remaining / 100)) + ' Hundred ';
      remaining %= 100;
    }
    if (remaining > 0) {
      words += numToWords(remaining);
    }

    words = words.trim() + ' Rupees';
    
    if (decimal > 0) {
      words += ' and ' + numToWords(decimal) + ' Paise';
    }

    return words + ' Only';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const previewInvoice = () => {
    calculateTotals(formData.items);
    setShowPreview(true);
    setShowHistory(false);
    setValidationErrors([]);
    setTimeout(() => {
      document.querySelector('.invoice-preview')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const editInvoice = () => {
    setShowPreview(false);
    setShowDownloadLink(false);
  };

  const viewInvoiceFromHistory = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPreview(true);
    setShowHistory(false);
    setShowDownloadLink(true);
  };

  const getStatusBadgeStyle = (status) => {
    const baseStyle = { ...styles.statusBadge };
    switch(status?.toLowerCase()) {
      case 'paid':
        return { ...baseStyle, ...styles.statusPaid };
      case 'overdue':
        return { ...baseStyle, ...styles.statusOverdue };
      default:
        return { ...baseStyle, ...styles.statusUnpaid };
    }
  };

  const resetForm = () => {
    setFormData({
      invoiceNumber: `INV-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      clientName: '',
      clientAddress: '',
      clientGST: '',
      placeOfSupply: '',
      amountInWords: '',
      subTotal: '0',
      sgstAmount: '0',
      cgstAmount: '0',
      cessAmount: '0',
      totalAmount: '0',
      note: 'Kindly note that this invoice for Services Provided remains unpaid. We request at least 50% payment to avoid service interruptions.',
      items: [
        {
          description: '',
          hsn: '',
          qty: '1',
          rate: '0',
          sgst: '9',
          cgst: '9',
          cess: '0',
          amount: '0'
        }
      ],
      status: 'unpaid'
    });
    setShowPreview(false);
    setSelectedInvoice(null);
    setShowDownloadLink(false);
    setValidationErrors([]);
  };

  // Save to database function
  const saveInvoiceToDatabase = async () => {
    setIsSaving(true);
    setPdfError(null);
    
    try {
      if (!validateForm()) {
        throw new Error('Please fix validation errors before saving');
      }

      const invoiceData = {
        invoiceNumber: formData.invoiceNumber,
        invoiceDate: new Date(formData.invoiceDate).toISOString(),
        dueDate: new Date(formData.dueDate).toISOString(),
        clientName: formData.clientName.trim(),
        clientAddress: formData.clientAddress.trim(),
        clientGST: formData.clientGST?.trim() || '',
        placeOfSupply: formData.placeOfSupply.trim(),
        amountInWords: formData.amountInWords,
        subTotal: parseFloat(formData.subTotal) || 0,
        sgstAmount: parseFloat(formData.sgstAmount) || 0,
        cgstAmount: parseFloat(formData.cgstAmount) || 0,
        cessAmount: parseFloat(formData.cessAmount) || 0,
        totalAmount: parseFloat(formData.totalAmount) || 0,
        note: formData.note?.trim() || '',
        status: formData.status,
        items: formData.items.map(item => ({
          description: item.description.trim(),
          hsn: item.hsn?.trim() || '',
          qty: parseFloat(item.qty) || 0,
          rate: parseFloat(item.rate) || 0,
          sgst: parseFloat(item.sgst) || 0,
          cgst: parseFloat(item.cgst) || 0,
          cess: parseFloat(item.cess) || 0,
          amount: parseFloat(item.amount) || 0
        })).filter(item => item.description !== '')
      };

      let savedInvoice;
      if (backendStatus === 'connected') {
        try {
          const response = await axios.post(`${API_URL}/invoices`, invoiceData);
          savedInvoice = response.data.data || response.data;
        } catch (axiosError) {
          throw new Error(axiosError.response?.data?.message || 'Failed to save to database');
        }
      } else {
        const localInvoices = JSON.parse(localStorage.getItem('invoiceHistory') || '[]');
        const newInvoice = {
          ...invoiceData,
          _id: Date.now().toString(),
          createdAt: new Date().toISOString()
        };
        localInvoices.unshift(newInvoice);
        localStorage.setItem('invoiceHistory', JSON.stringify(localInvoices));
        savedInvoice = newInvoice;
      }

      await loadInvoiceHistory();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      return savedInvoice;
    } catch (error) {
      console.error('Error saving invoice:', error);
      setPdfError(error.message || 'Failed to save invoice');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle PDF generation and save
  const handleGenerateAndSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    calculateTotals(formData.items);
    setShowDownloadLink(true);
    
    // Save to database
    try {
      await saveInvoiceToDatabase();
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  // Check backend connection on mount
  useEffect(() => {
    checkBackendConnection();
    loadCompanyDetails();
  }, []);

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          .invoice-container { padding: 15px !important; }
          .header-title { font-size: 1.5rem !important; }
          .btn { width: 100% !important; margin: 5px 0 !important; }
          .grid-2, .grid-2-1 { grid-template-columns: 1fr !important; }
          .table-responsive { margin: 0 -15px 15px -15px; }
          .action-buttons { flex-direction: column !important; }
          .mobile-note { display: block !important; }
        }
      `}</style>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div style={styles.deleteConfirmOverlay}>
          <div style={styles.deleteConfirmDialog}>
            <h3 style={{color: theme.dark, marginBottom: '15px'}}>Confirm Delete</h3>
            <p style={{marginBottom: '20px'}}>Are you sure you want to delete this invoice? This action cannot be undone.</p>
            <div style={{display: 'flex', gap: '10px', justifyContent: 'center'}}>
              <button style={{...styles.btn, ...styles.btnDanger}} onClick={confirmDelete}>Yes, Delete</button>
              <button style={{...styles.btn, ...styles.btnPrimary}} onClick={cancelDelete}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isGenerating && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingContent}>
            <div style={{marginBottom: '10px'}}>{isSaving ? 'Saving...' : 'Processing...'}</div>
            <div style={{width: '50px', height: '50px', border: '3px solid #f3f3f3', borderTop: `3px solid ${theme.primary}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto'}}></div>
          </div>
        </div>
      )}

      <div style={styles.header} className="no-print">
        <h1 style={styles.headerTitle}>Karmic Nexus IT Solutions - Invoice System</h1>
        <div style={styles.headerActions}>
          {backendStatus === 'offline' && (
            <span style={{color: '#e74c3c', fontSize: '12px', padding: '5px 10px', background: '#f8d7da', borderRadius: '4px'}}>
              Offline Mode
            </span>
          )}
          <button 
            style={{...styles.btn, ...styles.btnOutlinePrimary}}
            onClick={() => setShowCompanySettings(!showCompanySettings)}
          >
            ⚙️ Company Settings
          </button>
          <button 
            style={{...styles.btn, ...styles.btnOutlinePrimary}}
            onClick={() => navigate('/')}
          >
            ← Back
          </button>
        </div>
      </div>

      {/* Company Settings Panel */}
      {showCompanySettings && (
        <div style={styles.companySettings} className="no-print">
          <div style={styles.companySettingsHeader}>
            <h3 style={{margin: 0, color: theme.dark}}>Company Details</h3>
            <button style={{...styles.btn, ...styles.btnSuccess, ...styles.btnSm}} onClick={saveCompanyDetails}>
              💾 Save Settings
            </button>
          </div>
          
          {/* Logo Section */}
          <h4 style={{margin: '20px 0 10px', color: theme.dark}}>Company Logo</h4>
          <div style={styles.logoSection}>
            <div style={styles.logoPreview}>
              {companyDetails.logoData ? (
                <img src={companyDetails.logoData} alt="Company Logo" style={{width: '100%', height: '100%', objectFit: 'contain'}} />
              ) : companyDetails.logo ? (
                <img src={companyDetails.logo} alt="Company Logo" style={{width: '100%', height: '100%', objectFit: 'contain'}} />
              ) : (
                <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px'}}>☀️</div>
              )}
            </div>
            
            <div style={styles.logoInput}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Logo Image Path</label>
                <input type="text" style={styles.input} value={companyDetails.logo} onChange={handleLogoPathChange} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Or Upload Logo Image</label>
                <input type="file" ref={logoFileInputRef} style={styles.fileInput} accept="image/*" onChange={handleLogoUpload} id="logo-upload" />
                <div style={styles.logoUploadArea} onClick={() => logoFileInputRef.current?.click()}>
                  <span style={{fontSize: '24px'}}>📁</span>
                  <span>Click to upload logo image</span>
                </div>
              </div>
              {(companyDetails.logoData || companyDetails.logo) && (
                <button style={{...styles.btn, ...styles.btnDanger, ...styles.btnSm}} onClick={removeLogo}>
                  Remove Logo
                </button>
              )}
            </div>
          </div>

          <div style={styles.grid3}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Company Name</label>
              <input type="text" style={styles.input} value={companyDetails.companyName} onChange={(e) => handleCompanyDetailChange('companyName', e.target.value)} />
            </div>
          </div>

          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Address Line 1</label>
              <input type="text" style={styles.input} value={companyDetails.addressLine1} onChange={(e) => handleCompanyDetailChange('addressLine1', e.target.value)} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Address Line 2</label>
              <input type="text" style={styles.input} value={companyDetails.addressLine2} onChange={(e) => handleCompanyDetailChange('addressLine2', e.target.value)} />
            </div>
          </div>

          <div style={styles.grid3}>
            <div style={styles.formGroup}>
              <label style={styles.label}>GSTIN</label>
              <input type="text" style={styles.input} value={companyDetails.gstin} onChange={(e) => handleCompanyDetailChange('gstin', e.target.value)} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>MSME</label>
              <input type="text" style={styles.input} value={companyDetails.msme} onChange={(e) => handleCompanyDetailChange('msme', e.target.value)} />
            </div>
          </div>

          <h4 style={{margin: '20px 0 10px', color: theme.dark}}>Bank Details</h4>
          <div style={styles.grid3}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Bank Name</label>
              <input type="text" style={styles.input} value={companyDetails.bankName} onChange={(e) => handleCompanyDetailChange('bankName', e.target.value)} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Account Number</label>
              <input type="text" style={styles.input} value={companyDetails.accountNo} onChange={(e) => handleCompanyDetailChange('accountNo', e.target.value)} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Branch & IFSC</label>
              <input type="text" style={styles.input} value={companyDetails.branchIfsc} onChange={(e) => handleCompanyDetailChange('branchIfsc', e.target.value)} />
            </div>
          </div>
          
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Account Holder</label>
              <input type="text" style={styles.input} value={companyDetails.accountHolder} onChange={(e) => handleCompanyDetailChange('accountHolder', e.target.value)} />
            </div>
          </div>

          <h4 style={{margin: '20px 0 10px', color: theme.dark}}>QR Code</h4>
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Upload QR Code</label>
              <input type="file" ref={fileInputRef} style={styles.fileInput} accept="image/*" onChange={handleQRCodeUpload} id="qr-code-upload" />
              <div style={styles.qrCodeUploadArea} onClick={() => fileInputRef.current?.click()}>
                {companyDetails.qrCodeData ? (
                  <img src={companyDetails.qrCodeData} alt="QR Code" style={styles.qrCodePreview} />
                ) : (
                  <>
                    <div style={{fontSize: '40px', marginBottom: '10px'}}>📱</div>
                    <p>Click to upload QR Code image</p>
                  </>
                )}
              </div>
              {companyDetails.qrCodeData && (
                <button style={{...styles.btn, ...styles.btnDanger, ...styles.btnSm, marginTop: '10px'}} onClick={removeQRCode}>
                  Remove QR Code
                </button>
              )}
            </div>
          </div>

          <h4 style={{margin: '20px 0 10px', color: theme.dark}}>Contact Details</h4>
          <div style={styles.grid3}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Contact Number</label>
              <input type="text" style={styles.input} value={companyDetails.contact} onChange={(e) => handleCompanyDetailChange('contact', e.target.value)} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input type="email" style={styles.input} value={companyDetails.email} onChange={(e) => handleCompanyDetailChange('email', e.target.value)} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Working Hours</label>
              <input type="text" style={styles.input} value={companyDetails.workingHours} onChange={(e) => handleCompanyDetailChange('workingHours', e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {saveSuccess && (
        <div style={styles.successMessage} className="no-print">
          Invoice saved successfully!
        </div>
      )}
      
      {pdfError && (
        <div style={styles.errorMessage} className="no-print">
          {pdfError}
        </div>
      )}

      {validationErrors.length > 0 && (
        <div style={styles.validationErrors} className="no-print">
          <strong>Please fix the following errors:</strong>
          <ul style={{margin: '10px 0 0', paddingLeft: '20px'}}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={styles.tabContainer} className="no-print">
        <div 
          style={{
            ...styles.tab,
            ...(!showHistory && !showPreview ? styles.tabActive : styles.tabInactive)
          }}
          onClick={() => {
            setShowHistory(false);
            setShowPreview(false);
            resetForm();
          }}
        >
          Create New Invoice
        </div>
        <div 
          style={{
            ...styles.tab,
            ...(showHistory ? styles.tabActive : styles.tabInactive)
          }}
          onClick={() => {
            setShowHistory(true);
            setShowPreview(false);
            loadInvoiceHistory();
          }}
        >
          Invoice History {loadingHistory && <span style={styles.loadingSpinner}></span>}
        </div>
      </div>

      {/* Invoice Creation Form */}
      {!showHistory && !showPreview && (
        <div style={styles.invoiceContainer} className="invoice-container no-print">
          <form id="invoiceForm" onSubmit={(e) => e.preventDefault()}>
            <div style={styles.grid2}>
              <div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="invoiceNumber">Invoice #</label>
                  <input type="text" style={styles.input} id="invoiceNumber" value={formData.invoiceNumber} onChange={handleInputChange} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="invoiceDate">Invoice Date</label>
                  <input type="date" style={styles.input} id="invoiceDate" value={formData.invoiceDate} onChange={handleInputChange} required />
                </div>
              </div>
              <div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="dueDate">Due Date</label>
                  <input type="date" style={styles.input} id="dueDate" value={formData.dueDate} onChange={handleInputChange} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="status">Status</label>
                  <select style={styles.input} id="status" value={formData.status} onChange={handleInputChange}>
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div style={styles.grid2}>
              <div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="clientName">Bill To (Company Name) *</label>
                  <input type="text" style={{...styles.input, ...(!formData.clientName && validationErrors.length > 0 ? styles.inputError : {})}} id="clientName" placeholder="Enter company name" value={formData.clientName} onChange={handleInputChange} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="clientAddress">Address *</label>
                  <textarea style={{...styles.textarea, ...(!formData.clientAddress && validationErrors.length > 0 ? styles.inputError : {})}} id="clientAddress" rows="2" placeholder="Enter company address" value={formData.clientAddress} onChange={handleInputChange} required />
                </div>
              </div>
              <div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="clientGST">Client GSTIN</label>
                  <input type="text" style={styles.input} id="clientGST" placeholder="Enter client GSTIN" value={formData.clientGST} onChange={handleInputChange} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="placeOfSupply">Place of Supply *</label>
                  <input type="text" style={{...styles.input, ...(!formData.placeOfSupply && validationErrors.length > 0 ? styles.inputError : {})}} id="placeOfSupply" placeholder="Enter place of supply" value={formData.placeOfSupply} onChange={handleInputChange} required />
                </div>
              </div>
            </div>
            
            <h5 style={{marginBottom: '15px', color: theme.dark}}>Items</h5>
            <div style={styles.tableResponsive}>
              <div className="mobile-note" style={styles.mobileNote}>← Swipe to see all columns →</div>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Description *</th>
                    <th style={styles.th}>HSN</th>
                    <th style={styles.th}>Qty *</th>
                    <th style={styles.th}>Rate (₹)</th>
                    <th style={styles.th}>SGST %</th>
                    <th style={styles.th}>CGST %</th>
                    <th style={styles.th}>Cess %</th>
                    <th style={styles.th}>Amount (₹)</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index}>
                      <td style={styles.td}>{index + 1}</td>
                      <td style={styles.td}>
                        <input type="text" style={{...styles.inputSm, ...(!item.description && validationErrors.length > 0 ? styles.inputError : {})}} placeholder="Item description" value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} />
                      </td>
                      <td style={styles.td}>
                        <input type="text" style={styles.inputSm} placeholder="HSN Code" value={item.hsn} onChange={(e) => handleItemChange(index, 'hsn', e.target.value)} />
                      </td>
                      <td style={styles.td}>
                        <input type="number" style={{...styles.inputSm, ...(parseFloat(item.qty) <= 0 && validationErrors.length > 0 ? styles.inputError : {})}} placeholder="Qty" min="0.01" step="0.01" value={item.qty} onChange={(e) => handleItemChange(index, 'qty', e.target.value)} />
                      </td>
                      <td style={styles.td}>
                        <input type="number" style={styles.inputSm} placeholder="Rate" min="0" step="0.01" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', e.target.value)} />
                      </td>
                      <td style={styles.td}>
                        <input type="number" style={styles.inputSm} placeholder="SGST %" min="0" step="0.01" value={item.sgst} onChange={(e) => handleItemChange(index, 'sgst', e.target.value)} />
                      </td>
                      <td style={styles.td}>
                        <input type="number" style={styles.inputSm} placeholder="CGST %" min="0" step="0.01" value={item.cgst} onChange={(e) => handleItemChange(index, 'cgst', e.target.value)} />
                      </td>
                      <td style={styles.td}>
                        <input type="number" style={styles.inputSm} placeholder="Cess %" min="0" step="0.01" value={item.cess} onChange={(e) => handleItemChange(index, 'cess', e.target.value)} />
                      </td>
                      <td style={styles.td}>
                        <input type="number" style={styles.inputSm} placeholder="Amount" readOnly value={item.amount} />
                      </td>
                      <td style={styles.td}>
                        {formData.items.length > 1 && (
                          <button type="button" style={{...styles.btn, ...styles.btnDanger, ...styles.btnSm}} onClick={() => removeItem(index)}>🗑️</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div style={styles.mb3}>
              <button type="button" style={{...styles.btn, ...styles.btnOutlinePrimary}} onClick={addItem}>
                + Add Item
              </button>
            </div>
            
            <div style={styles.grid21}>
              <div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="amountInWords">Amount in Words</label>
                  <input type="text" style={styles.input} id="amountInWords" placeholder="Amount in words" value={formData.amountInWords} readOnly required />
                </div>
              </div>
              <div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="subTotal">Sub Total (₹)</label>
                  <input type="number" style={styles.input} id="subTotal" readOnly value={formData.subTotal} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="sgstAmount">SGST Amount (₹)</label>
                  <input type="number" style={styles.input} id="sgstAmount" readOnly value={formData.sgstAmount} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="cgstAmount">CGST Amount (₹)</label>
                  <input type="number" style={styles.input} id="cgstAmount" readOnly value={formData.cgstAmount} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="cessAmount">Cess Amount (₹)</label>
                  <input type="number" style={styles.input} id="cessAmount" readOnly value={formData.cessAmount} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="totalAmount">Total Amount (₹)</label>
                  <input type="number" style={styles.input} id="totalAmount" readOnly value={formData.totalAmount} />
                </div>
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="note">Note</label>
              <textarea style={styles.textarea} id="note" rows="2" value={formData.note} onChange={handleInputChange} />
            </div>
            
            <div style={styles.actionButtons}>
              <button type="button" style={{...styles.btn, ...styles.btnPrimary}} onClick={previewInvoice} disabled={isGenerating}>
                Preview Invoice
              </button>
              <button type="button" style={{...styles.btn, ...styles.btnSuccess}} onClick={handleGenerateAndSave} disabled={isGenerating}>
                💾 {isGenerating ? 'Processing...' : 'Save & Generate PDF'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Invoice History */}
      {showHistory && (
        <div style={styles.historySection} className="no-print">
          <h3 style={{color: theme.dark, marginBottom: '20px'}}>Invoice History</h3>
          {loadingHistory ? (
            <div style={{textAlign: 'center', padding: '40px'}}>
              <div style={styles.loadingSpinner}></div>
              <p style={{marginTop: '10px', color: '#666'}}>Loading invoices...</p>
            </div>
          ) : invoiceHistory.length === 0 ? (
            <p style={{textAlign: 'center', color: '#999', padding: '40px'}}>No invoices found</p>
          ) : (
            invoiceHistory.map((invoice) => (
              <div key={invoice._id || invoice.id || Math.random().toString()} style={styles.historyItem} onClick={() => viewInvoiceFromHistory(invoice)}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px'}}>
                  <div style={{flex: 1}}>
                    <strong>{invoice.invoiceNumber}</strong> - {invoice.clientName}
                    <div style={{fontSize: '12px', color: '#666'}}>
                      Created: {formatDateTime(invoice.createdAt)}
                    </div>
                  </div>
                  <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                    <span style={getStatusBadgeStyle(invoice.status)}>
                      {invoice.status ? invoice.status.toUpperCase() : 'UNPAID'}
                    </span>
                    <span style={{fontWeight: 'bold'}}>₹{parseFloat(invoice.totalAmount).toFixed(2)}</span>
                    <button style={{...styles.btn, ...styles.btnDanger, ...styles.btnSm}} onClick={(e) => deleteInvoice(invoice._id || invoice.id, e)}>🗑️</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      
      {/* Invoice Preview with PDF Download */}
      {showPreview && (
        <div style={styles.invoicePreview} className="invoice-preview">
          <div style={styles.textCenter} className="no-print">
            <h3 style={{margin: '0 0 15px', color: theme.dark}}>
              {selectedInvoice ? 'Historical Invoice' : 'Invoice Preview'}
            </h3>
          </div>
          
          {/* PDF Download Link */}
          {showDownloadLink && (
            <div style={styles.downloadLinkContainer} className="no-print">
              <PDFDownloadLink
                document={<InvoicePDFDocument data={selectedInvoice || formData} companyDetails={companyDetails} formatDate={formatDate} />}
                fileName={`Invoice_${(selectedInvoice || formData).invoiceNumber}.pdf`}
                style={{ textDecoration: 'none' }}
              >
                {({ loading }) => (
                  <button style={{...styles.btn, ...styles.btnSuccess}} disabled={loading}>
                    {loading ? 'Generating PDF...' : '⬇️ Download PDF'}
                  </button>
                )}
              </PDFDownloadLink>
            </div>
          )}
          
          {/* Simple HTML Preview (optional - you can remove this if you don't need preview) */}
          <div style={{ marginTop: '20px', border: '1px solid #ccc', padding: '20px' }}>
            <h4>Invoice Summary</h4>
            <p><strong>Invoice #:</strong> {(selectedInvoice || formData).invoiceNumber}</p>
            <p><strong>Client:</strong> {(selectedInvoice || formData).clientName}</p>
            <p><strong>Total:</strong> ₹{(selectedInvoice || formData).totalAmount}</p>
            <p><strong>Items:</strong> {(selectedInvoice || formData).items.length}</p>
          </div>
          
          <div style={{...styles.actionButtons, marginTop: '20px'}} className="action-buttons no-print">
            {!selectedInvoice && (
              <button type="button" style={{...styles.btn, ...styles.btnPrimary}} onClick={editInvoice} disabled={isGenerating}>
                Edit Invoice
              </button>
            )}
            {selectedInvoice && (
              <button type="button" style={{...styles.btn, ...styles.btnOutlinePrimary}} onClick={() => {
                setSelectedInvoice(null);
                setShowPreview(false);
                setShowHistory(true);
              }}>
                Back to History
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewInvoice;